/**
 * The language menu (owner, 2026-10-30: the native select looked dated). A pill with the globe and the current code;
 * a small panel drops from it with each language's code and native name, the current one ticked. Keyboard: arrows move,
 * Enter picks, Escape closes; a click outside closes. `paper` is the landing hero's light look, the default follows
 * the app theme.
 */
import { LANG_META, LANGS, type Lang } from "@prop/i18n";
import { cn, useReducedMotion } from "@prop/ui";
import { AnimatePresence, m } from "framer-motion";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useLang } from "../i18n";

export function LangMenu({ label, paper = false, className }: { label: string; paper?: boolean; className?: string }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const id = useId();
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    items.current[LANGS.indexOf(lang)]?.focus();
    return () => document.removeEventListener("pointerdown", away);
  }, [open, lang]);

  const pick = (l: Lang) => {
    setLang(l);
    setOpen(false);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return setOpen(false);
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const at = items.current.indexOf(document.activeElement as HTMLButtonElement);
    const next = (at + (e.key === "ArrowDown" ? 1 : -1) + LANGS.length) % LANGS.length;
    items.current[next]?.focus();
  };

  return (
    <div ref={root} className={cn("relative", className)} onKeyDown={onKey}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          paper ? "border-[#030407]/15 bg-white/70 text-[#030407] hover:bg-white" : "border-border bg-background text-foreground hover:bg-muted",
        )}
      >
        <Globe className="size-4 opacity-70" aria-hidden />
        <span className="tabular-nums">{LANG_META[lang].code}</span>
        <ChevronDown className={cn("size-3.5 opacity-60 transition-transform duration-200", open && "rotate-180")} aria-hidden />
      </button>
      <AnimatePresence>
        {open && (
          <m.div
            id={id}
            role="menu"
            aria-label={label}
            initial={reduced ? false : { opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ transformOrigin: "top right" }}
            className={cn(
              "absolute right-0 top-[calc(100%+8px)] z-50 flex min-w-48 flex-col gap-0.5 rounded-[14px] border p-1.5 shadow-[0_24px_48px_-20px_rgba(0,0,0,0.45)]",
              paper ? "border-[#030407]/10 bg-white text-[#030407]" : "border-border bg-popover text-popover-foreground",
            )}
          >
            {LANGS.map((l, i) => {
              const on = l === lang;
              return (
                <button
                  key={l}
                  ref={(el) => {
                    items.current[i] = el;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  lang={LANG_META[l].tag}
                  onClick={() => pick(l)}
                  className={cn(
                    "flex min-h-10 items-center gap-3 rounded-[10px] px-2.5 text-left text-sm transition-colors duration-150 focus-visible:outline-none",
                    paper ? "hover:bg-[#030407]/[0.06] focus-visible:bg-[#030407]/[0.06]" : "hover:bg-muted focus-visible:bg-muted",
                    on && "font-semibold",
                  )}
                >
                  <span className={cn("grid h-6 w-8 place-items-center rounded-md text-[11px] font-bold", on ? "bg-[#d4ff24] text-[#030407]" : paper ? "bg-[#030407]/[0.07]" : "bg-muted")}>{LANG_META[l].code}</span>
                  <span className="flex-1">{LANG_META[l].native}</span>
                  {on && <Check className="size-4" aria-hidden />}
                </button>
              );
            })}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
